import { Suspense, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { useHotkey } from '../hooks/useHotkey'
import { useIsMobile } from '../hooks/useMediaQuery'
import { Icon } from '../components/ui/Icon'
import { TourPilot } from '../features/tours/TourPilot'
import { NavRail } from './NavRail'
import { TopBar, MobileRangeBar } from './TopBar'
import { NowRail } from './NowRail'
import { CommandPalette } from './CommandPalette'
import { RouteFallback } from './RouteFallback'

/**
 * The authenticated frame.
 *
 *   ┌────┬──────────────────────────────┐
 *   │    │  TopBar        chrome-900 56 │
 *   │ ▤  ├──────────────────────────────┤
 *   │ 64 │  NowRail       chrome-800 28 │
 *   │    ├──────────────────────────────┤
 *   │    │  paper canvas, max 1440      │
 *   └────┴──────────────────────────────┘
 *
 * Under 768 px the rail moves to a fixed bottom bar and the canvas gains room for it.
 */
export const AppShell = (): ReactElement => {
  const t = useT()
  const { activeBrand } = useAuth()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = useLocation()
  const mainRef = useRef<HTMLElement | null>(null)
  const firstRender = useRef(true)

  useHotkey({ key: 'k', meta: true }, () => {
    setPaletteOpen((previous) => !previous)
  })

  /**
   * A route change nobody sent focus anywhere for leaves it on `<body>` — reachable by Tab from
   * nothing, in practice a keyboard user dropped back to the top of the page. Most commonly: the
   * command palette closes (restoring focus to whatever was focused before it opened) and *then*
   * fires its `navigate()`, which can unmount the very element the restore just landed on.
   *
   * Only steps in when focus has nowhere to go — `document.activeElement === body` — which is
   * also why switching an in-page tab (itself a URL change, e.g. Analytics' network tabs) is
   * untouched: `Tabs.tsx` has already moved focus to the newly active tab by the time this runs,
   * so the guard sees a real focus target and leaves it alone.
   */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (document.activeElement === document.body) {
      mainRef.current?.focus()
    }
  }, [location.pathname])

  return (
    <div className="flex min-h-dvh bg-paper">
      {/* First thing in the tab order: the rail is nine links deep before the canvas starts. */}
      <a
        href="#main"
        className="ring-focus sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-90 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2 focus:text-ink"
      >
        {t('common.shell.skipToContent')}
      </a>

      <NavRail />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-30">
          <TopBar
            onOpenCommandPalette={() => {
              setPaletteOpen(true)
            }}
          />
          <NowRail />
        </div>

        {isMobile ? <MobileRangeBar /> : null}

        <main
          id="main"
          ref={mainRef}
          tabIndex={-1}
          className={cn(
            'min-w-0 flex-1 outline-none md:pb-0',
            /* pb-20 clears the bottom nav bar alone. The compose FAB below adds its own h-14
               (56px) plus a 16px gap above that bar, so content needs that same footprint again
               (plus a little breathing room) or it ends up scrolled in underneath the button —
               see the matching comment on the FAB's own `bottom` value. */
            isMobile && activeBrand ? 'pb-[calc(5rem+88px)]' : 'pb-20',
          )}
        >
          <div className="page-shell py-6">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false)
        }}
      />

      {isMobile && activeBrand ? (
        <Link
          to="/app/planner?compose=1"
          aria-label={t('planner.toolbar.compose')}
          /* h-14 (56px) + the 16px gap above the nav bar below — `main`'s pb-[calc(5rem+88px)]
             above reserves this same 56+16(+16 breathing room) footprint so scrollable content
             never sits under this button. */
          className="ring-focus fixed right-4 bottom-[calc(56px+env(safe-area-inset-bottom)+16px)] z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brass text-chrome-900 shadow-lg transition-colors hover:bg-brass-deep hover:text-chrome-ink active:bg-brass-deep"
        >
          <Icon name="plus" size={22} />
        </Link>
      ) : null}

      {/* Above everything else in this frame, and the one thing here that has to survive the
          navigation it causes itself — see `features/tours`. */}
      <TourPilot />
    </div>
  )
}
